import React, { useMemo } from 'react';
import { Tooltip, Avatar } from 'antd';
import { Cloud } from 'lucide-react';

import DataTable from '../../../../../../../shared/designSystem/components/DataTable';
import { TransactionPublisherExpandedCard } from './TransactionPublisherExpandedCard';
import { formatWholeNumber } from '../../../../../../../../core/utils/campaigns';

// =============================================================================
// TransactionPublisherTable (V1) — migrated to shared DataTable. Same column
// model as before: color · optional image · Name · Impressions · Transactions.
//
// Name column decodes HTML entities server-side could include, collapses
// "Creative…" rows to "Unknown publisher", and truncates at 45 chars with a
// tooltip. Row filter drops the legacy `[tail aggregate]` rows that V1's
// upstream pipeline emits but shouldn't surface to the user.
// =============================================================================

const sortString = (key) => (a, b) =>
  String(a.original[key] || '').localeCompare(String(b.original[key] || ''));
const sortNumber = (key) => (a, b) =>
  (Number(a.original[key]) || 0) - (Number(b.original[key]) || 0);

const decodeName = (text) => {
  if (!text) return '';
  const parser = new DOMParser();
  const parsed = parser.parseFromString(text, 'text/html');
  return parsed.body.textContent || '';
};

const TransactionPublisherTable = ({ publisherData = [] }) => {
  const rows = useMemo(
    () =>
      publisherData
        .filter((p) => p.xAxis !== '[tail aggregate]')
        .map((p, i) => ({ ...p, __key: i })),
    [publisherData]
  );

  const hasImages = useMemo(
    () => publisherData.some((p) => p && p.imageUrl),
    [publisherData]
  );

  const columns = useMemo(() => {
    const color = {
      id: 'color',
      accessorKey: 'color',
      header: '',
      sortable: false,
      meta: { align: 'center', width: 32 },
      cell: ({ getValue }) => (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: 9999,
            background: getValue() || '#cbd5e1',
          }}
        />
      ),
    };

    const image = {
      id: 'imageUrl',
      accessorKey: 'imageUrl',
      header: '',
      sortable: false,
      meta: { align: 'left', width: 68 },
      cell: ({ getValue }) => {
        const url = getValue();
        return (
          <Avatar
            shape="square"
            size={44}
            src={url || undefined}
            icon={!url ? <Cloud size={18} strokeWidth={2} /> : undefined}
          />
        );
      },
    };

    const name = {
      id: 'value',
      accessorKey: 'value',
      header: 'Name',
      sortable: true,
      sortingFn: sortString('value'),
      meta: { align: 'left' },
      cell: ({ getValue }) => {
        const raw = getValue();
        if (!raw) return null;
        const decoded = decodeName(raw);
        const pubUnknown = decoded.includes('Creative');
        const display = pubUnknown ? 'Unknown publisher' : decoded;
        if (display.length > 45) {
          return (
            <Tooltip overlayClassName="mjl-ds" title={display}>
              <span>{display.slice(0, 46)}…</span>
            </Tooltip>
          );
        }
        return <span>{display}</span>;
      },
    };

    const impressions = {
      id: 'impressions',
      accessorKey: 'impressions',
      header: 'Impressions',
      sortable: true,
      sortingFn: sortNumber('impressions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? formatWholeNumber(v) : 0}</span>;
      },
    };

    const transactions = {
      id: 'totalTransactions',
      accessorKey: 'totalTransactions',
      header: 'Transactions',
      sortable: true,
      sortingFn: sortNumber('totalTransactions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? formatWholeNumber(v) : 0}</span>;
      },
    };

    return hasImages ? [color, image, name, impressions, transactions] : [color, name, impressions, transactions];
  }, [hasImages]);

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      pageSizeOptions={[10, 20, 50, 100]}
      expandable={{
        isRowExpandable: (record) =>
          Boolean(record.extra) ||
          (record.media && record.media.type) === 'THREEDEE',
        renderExpandedRow: (record) => <TransactionPublisherExpandedCard record={record} />,
      }}
      emptyState="No publishers yet."
    />
  );
};

export default TransactionPublisherTable;
